import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, AlertCircle, Clock, Truck, Database, Bell } from "lucide-react";

const SEVERITY = {
  critical:     { label: "Critical",     icon: AlertCircle,    pill: "bg-rose-100 text-rose-800",      bar: "bg-rose-500" },
  high:         { label: "High",         icon: AlertTriangle,  pill: "bg-amber-100 text-amber-800",    bar: "bg-amber-500" },
  medium:       { label: "Medium",       icon: Clock,          pill: "bg-yellow-100 text-yellow-800",  bar: "bg-yellow-500" },
  info:         { label: "Idle Trucks",  icon: Truck,          pill: "bg-blue-100 text-blue-800",      bar: "bg-blue-500" },
  data_quality: { label: "Data Quality", icon: Database,       pill: "bg-slate-100 text-slate-800",    bar: "bg-slate-500" },
};

const ORDER_KEYS = ["critical", "high", "medium", "info", "data_quality"];

export default function PriorityAlertsPanel({ canSeeRevenue = true }) {
  const [data, setData] = useState(null);
  const [activeKey, setActiveKey] = useState("critical");
  const [err, setErr] = useState("");

  async function load() {
    try { const r = await api.get("/dashboard/alerts"); setData(r.data); }
    catch (e) { setErr(e?.response?.data?.detail || "Could not load alerts"); }
  }
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);

  if (err) return <Card className="p-4 border border-rose-200 bg-rose-50/50 text-sm text-rose-800" data-testid="alerts-panel-error">{err}</Card>;
  if (!data) return <Card className="p-4 border border-slate-200 shadow-none rounded-xl text-sm text-slate-400" data-testid="alerts-panel-loading">Loading priority alerts…</Card>;

  const counts = data.counts || {};
  const total = data.total || 0;
  const items = (data.alerts || {})[activeKey] || [];
  const ActiveIcon = SEVERITY[activeKey]?.icon || Bell;

  return (
    <Card className="p-4 border border-slate-200 shadow-none rounded-xl" data-testid="alerts-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Bell className="h-4 w-4 text-slate-700" />
          <h3 className="heading text-[15px] font-semibold text-slate-900">Priority Alerts</h3>
          {total === 0 && <Badge className="bg-emerald-100 text-emerald-800">All clear</Badge>}
          {total > 0 && <Badge className="bg-rose-50 text-rose-800 border border-rose-200">{total} open</Badge>}
        </div>
        <div className="text-[11px] text-slate-500" title={`Generated at ${data.generated_at}`}>
          SLAs · stuck&gt;{data.thresholds?.critical_submitted_or_hold_days}d / prod&gt;{data.thresholds?.high_production_days}d / pay&gt;{data.thresholds?.medium_payment_pending_days}d
        </div>
      </div>

      {/* Severity tabs */}
      <div className="flex gap-1.5 flex-wrap mb-3">
        {ORDER_KEYS.map((k) => {
          const c = counts[k] || 0;
          const meta = SEVERITY[k];
          const Icon = meta.icon;
          const active = activeKey === k;
          return (
            <button
              key={k}
              onClick={() => setActiveKey(k)}
              className={`px-2.5 py-1.5 rounded-md text-[12px] flex items-center gap-1.5 border transition-colors
                ${active ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}
              data-testid={`alert-tab-${k}`}
            >
              <Icon className="h-3.5 w-3.5" />
              <span>{meta.label}</span>
              <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${active ? "bg-white text-slate-900" : meta.pill}`}>{c}</span>
            </button>
          );
        })}
      </div>

      {/* Item list */}
      <div className="max-h-[320px] overflow-auto" data-testid={`alerts-list-${activeKey}`}>
        {items.length === 0 ? (
          <div className="text-[12px] text-slate-400 italic py-3">No alerts in this bucket.</div>
        ) : (
          <table className="w-full text-[12px]">
            <thead className="text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-2 py-1 text-left">{activeKey === "info" ? "Truck" : activeKey === "data_quality" ? "Issue" : "PFI / Distributor"}</th>
                {activeKey !== "data_quality" && <th className="px-2 py-1 text-left">{activeKey === "info" ? "Status" : "Status"}</th>}
                {activeKey !== "data_quality" && <th className="px-2 py-1 text-right">{activeKey === "info" ? "Idle" : (activeKey === "medium" ? "Overdue" : "Stuck")}</th>}
                {canSeeRevenue && (activeKey === "critical" || activeKey === "high" || activeKey === "medium") && <th className="px-2 py-1 text-right">Value</th>}
              </tr>
            </thead>
            <tbody>
              {items.slice(0, 50).map((it, idx) => (
                <tr key={idx} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`alert-row-${activeKey}-${idx}`}>
                  {activeKey === "info" ? (
                    <>
                      <td className="px-2 py-1 font-medium text-slate-800">
                        <div>{it.truck_label}</div>
                        <div className="text-[10.5px] text-slate-500">{it.vehicle_number}</div>
                      </td>
                      <td className="px-2 py-1 text-slate-500">{it.status}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{it.idle_days}d</td>
                    </>
                  ) : activeKey === "data_quality" ? (
                    <td colSpan={3} className="px-2 py-1.5 text-slate-700">{it.message}</td>
                  ) : (
                    <>
                      <td className="px-2 py-1">
                        <Link to={`/orders/${it.order_id}`} className="font-medium text-slate-900 hover:underline">{it.pfi_number || it.order_id?.slice(0, 8)}</Link>
                        <div className="text-[10.5px] text-slate-500 truncate max-w-[260px]">{it.distributor || "—"}</div>
                      </td>
                      <td className="px-2 py-1 text-slate-600">{it.status}</td>
                      <td className="px-2 py-1 text-right tabular-nums text-slate-700">{(it.stuck_days ?? it.overdue_days)}d</td>
                      {canSeeRevenue && (activeKey === "critical" || activeKey === "high" || activeKey === "medium") && (
                        <td className="px-2 py-1 text-right tabular-nums text-slate-700">{formatINR(it.grand_total ?? 0)}</td>
                      )}
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {items.length > 50 && (
          <div className="text-[11px] text-slate-500 mt-2">Showing top 50 of {items.length} · oldest first</div>
        )}
      </div>
    </Card>
  );
}
